/**
 * Invoice Binding Service - Frontend service for orphan invoice linking
 */

import api from "../../../shared/api.jsx";

/**
 * Get orphan invoices (missing jobId or job doesn't exist)
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of results
 * @param {number} options.skip - Number of results to skip
 * @param {string} options.customerId - Optional customer filter
 * @returns {Promise<Object>} { success: boolean, data: Array, count: number, total: number }
 */
export const getOrphanInvoices = async (options = {}) => {
  try {
    const { limit = 50, skip = 0, customerId } = options;
    const params = {
      limit,
      skip,
      ...(customerId ? { customerId } : {}),
    };

    const response = await api.get("/invoices/orphans", { params });
    return {
      success: true,
      data: response.data?.data || response.data || [],
      count: response.data?.count || 0,
      total: response.data?.total || 0,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || "Failed to get orphan invoices",
      data: [],
      count: 0,
      total: 0,
    };
  }
};

/**
 * Bind invoice(s) to job(s)
 * @param {Array<Object>} bindings - Array of { invoiceId, jobId }
 * @param {string} mode - "soft" or "hard" (default: "soft")
 * @param {string} note - Optional note
 * @returns {Promise<Object>} { success: boolean, results: Array, summary: Object }
 */
export const bindInvoicesToJobs = async (bindings, mode = "soft", note = null) => {
  try {
    if (!Array.isArray(bindings) || bindings.length === 0) {
      return {
        success: false,
        error: "Bindings array is required and must not be empty",
        results: [],
      };
    }

    const response = await api.post("/invoices/bind-job", {
      bindings,
      mode,
      note,
    });

    return {
      success: response.data?.success || true,
      results: response.data?.results || [],
      summary: response.data?.summary || {
        total: bindings.length,
        successful: 0,
        failed: 0,
      },
      error: response.data?.error || null,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || "Failed to bind invoices to jobs",
      results: [],
      summary: {
        total: bindings.length,
        successful: 0,
        failed: bindings.length,
      },
    };
  }
};
